<script lang="ts" setup>
	import { useDebounceFn } from '@vueuse/core'

	const privacyContent = [
		{
			key: 'introduction',
			name: 'Introduction',
			content: `
      <p class='text-lg font-medium mb-3'>
       MyCoverGenius Limited (“MCG”, “we”, “our”, “us”) values your privacy and is committed to protecting your personal information. This Privacy Policy explains how we collect, use, store, disclose, and safeguard your information when you use our platforms, products, and services. By using our website, mobile application, or any of our services, you consent to the practices described in this policy.
      </p>
    `
		},
		{
			key: 'information',
			name: 'Information We Collect',
			content: `
      <p class='text-lg font-medium mb-3'>
       We collect different types of information to provide our services effectively and comply with regulatory requirements:
      </p>`,
			subList: [
				{
					key: 'personal-information',
					name: 'Personal Information',
					content: `
                  <ol class='text-base font-medium mb-3 list-item sublist space-y-2'>
                    <li>
                        Full name
                    </li>
                    <li>
                        Email address
                    </li>
                    <li>
                        Phone number
                    </li>
                    <li>
                        Gender
                    </li>
                    <li>
                        Date of birth
                    </li>
                    <li>
                        Residential address
                    </li>
                    <li>
                        National Identification Number (NIN)
                    </li>
                    <li>
                        Corporate Affairs Commission (CAC) details for businesses
                    </li>
                    <li>
                        Next of kin or beneficiary information (where applicable)
                    </li>
                  </ol>`
				},
				{
					key: 'usage-information',
					name: 'Usage Information',
					content: `
          <ul class='text-base font-medium mb-3 list-item  space-y-2'>
            <li>
                Device information (type, OS version, unique device identifiers)
            </li>
            <li>
               Log data and activity within the app
            </li>
            <li>
               IP address and general device data
            </li>
            <li>
                Usage analytics (e.g, how you navigate and use the platform to help us improve features and services)
            </li>
            <li>
               Push notification tokens (for sending relevant alerts, reminders, and updates)
            </li>
          </ul>`
				},
				{
					key: 'location-data',
					name: 'Location Data',
					content: `
          <ul class='text-base font-medium mb-3 list-item space-y-2'>
            <li>
                For identity verification and regulatory compliance, we collect data from official sources, including NIN records, CAC (for corporate accounts), and other KYC tools. This may include biometric-linked data from NIMC verification services.
            </li>
           
          </ul>`
				}
			]
		},
		{
			key: 'how-we-collect-information',
			name: 'How We Collect Information',
			content: `
      <p class='text-lg font-medium mb-3'>We collect data through:</p>
      <ol class='text-base font-medium mb-3 list-item  sublist space-y-2'>
        <li>
            Direct input: When you register, purchase plans, submit claims, or contact support.
        </li>
        <li>
            Automated technologies: When you interact with our website or app (cookies, analytics tools, etc.).
        </li>
        <li>
            Third parties: Verification service providers, analytics platforms, and regulatory bodies.
        </li>
      </ol>
    `
		},
		{
			key: 'how-we-use-your-information',
			name: 'How We Use Your Information',
			content: `
      <p class='text-lg font-medium mb-3'>We use your information to:</p>

      <ol class='text-base font-medium mb-3 list-item sublist space-y-2'>
        <li>
            Create and manage your account or policy
        </li>
        <li>
            Verify identity and perform KYC checks
        </li>
        <li>
            Deliver insurance products and services
        </li>
        <li>
            Process payments and manage subscriptions
        </li>
        <li>
            Send important service updates, push notifications, and reminders
        </li>
        <li>
            Handle claims processing and regulatory reporting
        </li>
        <li>
            Improve our platform through usage analytics and feedback
        </li>
        <li>
            Comply with legal and regulatory obligations
        </li>
      </ol>
      <p class='text-lg font-medium mb-3 border-b w-full pb-10'>We do not sell your personal data.</p>
    `
		},
		{
			key: 'data-sharing-disclosure',
			name: ' Data Sharing & Disclosure',
			content: `
      <p class='text-lg font-medium mb-3'>We may share your information with:</p>
      <ol class='text-base font-medium mb-3 list-item sublist space-y-2'>
        <li>
            Insurance partners and underwriters for policy issuance and claims settlement
        </li>
        <li>
            Regulatory bodies (e.g., NAICOM) as legally required
        </li>
        <li>
            Service providers that help us run operations (e.g., cloud storage, analytics, payment gateways, KYC providers)
        </li>
        <li>
            Third parties during mergers, acquisitions, or legal transfers, with appropriate safeguards in place
        </li>
      </ol>
      <p class='text-lg font-medium  pb-10 border-b w-full'>We ensure all partners comply with applicable data protection regulations and maintain confidentiality.</p>
    `
		},
		{
			key: 'international-data-transfers',
			name: 'International Data Transfers',
			content: `
      <p class='text-lg font-medium mb-3'>
        Some of our service providers and partners are located outside Nigeria. Where your personal data is transferred internationally, we implement appropriate safeguards (e.g., encryption, standard contractual clauses) to protect your information in line with the Nigeria Data Protection Regulation (NDPR) and other applicable laws.
      </p>
    `
		},
		{
			key: 'how-we-use-your-personal-information',
			name: 'How we use your personal information',
			content: `
      <p class='text-lg font-medium mb-3'>
        We retain your personal data for as long as:
      </p>
      <ol class="list-item sublist text-base my-5 space-y-3 mt-2">
        <li>
            You maintain an active account or policy with us
        </li>
        <li>
            Required to comply with legal, tax, and regulatory obligations
        </li>
        <li>
          Necessary to resolve disputes or enforce agreements
        </li>
      </ol>
      <p class='text-base my-2.5'>When you request account deletion:</p>
      <ul class="list-item sublist text-base my-5 space-y-3 mt-2">
        <li>
            Your account will be deactivated, and your personal information will be permanently deleted or anonymized within a reasonable period (except for any data that we are required to retain by law).
        </li>
        <li>
            Some records (e.g., claims history, regulatory submissions) may be retained for the legally required retention period even after account deletion.
        </li>
      </ul>
      <p class='text-base mt-2.5  pb-10 border-b w-full'>You can initiate deletion by contacting our support team at <a href="mailto:hello@mycovergenius.com" class='text-base-primary'>hello@mycovergenius.com</a></p>
    `
		},
		{
			key: 'childrens-data',
			name: "Children's Data",
			content: `
      <p class='text-lg font-medium mb-3'>
        Our services are not directed at children under 18. However, dependents under 18 may be added to family plans by a parent or legal guardian.
      </p>
      <ol class="list-item sublist text-base my-5 space-y-3 mt-2 border-b  pb-10">
        <li>
            We collect limited personal information about dependents (e.g., name, age, gender, relationship to policyholder) only for policy and claims purposes.
        </li>
        <li>
            We rely on the consent and responsibility of the parent/guardian to provide and manage this data.
        </li>
        <li>
            We do not knowingly collect data directly from children under 18 without guardian consent.
        </li>
      </ol>
    `
		},
		{
			key: 'data-protection',
			name: 'Your Data Protection Rights',
			content: `
      <p class='text-lg font-medium mb-3'>
        Under NDPR and other applicable laws, you have the right to:
      </p>
      <ol class="list-item sublist text-base my-5 space-y-3 mt-2">
        <li>
            Access your personal data
        </li>
        <li>
            Request correction or updates
        </li>
        <li>
          Request deletion (subject to legal exceptions)
        </li>
        <li>
          Withdraw consent at any time
        </li>
        <li>
          Object to processing for certain purposes
        </li>
        <li>
          Lodge complaints with regulatory authorities
        </li>
      </ol>
      <p class='text-base  pb-10 pt-2.5 border-b w-full'>
          To exercise any of these rights, contact us at <a href="mailto:hello@mycovergenius.com" class='text-base-primary'>hello@mycovergenius.com</a>
      </p>
    `
		},
		{
			key: 'data-security',
			name: 'Data Security',
			content: `
      <p class='text-lg font-medium  pb-10 border-b'>
        We use administrative, technical, and physical security measures to protect your data, including encryption, secure storage, access controls, and regular security audits. While we strive to protect your information, no system is completely secure.
      </p>`
		},
		{
			key: 'telemedicine-services',
			name: 'Telemedicine Services',
			content: `
			<p class='text-lg font-medium mb-3'>
				If you use our telemedicine services, we will process the personal information necessary to verify your eligibility and facilitate access to virtual healthcare services.
			</p>
			<p class='text-lg font-medium mb-3'>
				Virtual consultations are provided by our third-party partners. To deliver these services, we securely transmit only the information required to our approved partners. MyCoverGenius does not store your consultation records, medical history, diagnoses, prescriptions, consultation summaries, or other clinical information within its internal systems. These records are collected, processed, and retained by our healthcare partners in accordance with applicable legal and regulatory requirements.
			</p>
			<p class='text-lg font-medium mb-3'>
				Consultation summaries are made available only to you through the telemedicine service. MyCoverGenius does not access or retain these summaries, except where required by law or with your explicit consent.
			</p>
			<p class='text-lg font-medium mb-3'>
				We may receive and display limited service information, such as consultation and prescription fulfilment status, solely to support your experience and provide customer assistance.
			</p>
			<p class='text-lg font-medium  pb-10 border-b w-full'>
				By using our telemedicine services, you consent to the processing and sharing of your personal and health information with our approved partners for providing healthcare services. We process personal data in accordance with our licence issued by the Nigeria Data Protection Commission (NDPC).
			</p>
			`
		},

		{
			key: 'updates-to-this-policy',
			name: 'Updates to This Policy',
			content: `
      <p class='text-lg font-medium  pb-10 border-b'>
        We reserve the right to update this Privacy Policy periodically. Changes will be posted on this page with an updated Effective Date. We encourage you to review this policy regularly to stay informed.
      </p>`
		},
		{
			key: 'contact-us',
			name: 'Contact Us',
			content: `
      <p class='text-lg font-medium pb-3'>
       If you have any questions, complaints, or concerns about this Privacy Policy or our data practices, contact us at:
      </p>
      <strong class='font-semibold text-lg pb-1'>
          MyCoverGenius
      </strong>
      <p class='text-gray-500 text-base'>Address: Venia Place, Plot 8, Providence Street, Lekki Phase 1, Lagos, Nigeria</p>
      <p class='text-gray-500 text-base'>Customer Care Hotline: 08099988005</p>
      <p class='text-gray-500 text-base'>Email: hello@mycovergenius.com</p>
      `
		}
	]
	const { observeElementInViewport } = useInterSector()
	const inView = shallowRef('')

	const setScroll = useDebounceFn(() => {
		privacyContent.forEach((item, index) => {
			const title = document.querySelector(`#${item.key}`)
			if (title) {
				return observeElementInViewport(
					title,
					isVisible => {
						if (isVisible) {
							inView.value = item.key
						}
					},
					1.0
				)
			}
		})
	}, 100)

	onMounted(() => {
	if (import.meta.client) {
		document.addEventListener('scroll', setScroll)

		const route = useRoute()
		if (route.hash) {
			nextTick(() => {
				setTimeout(() => {
					const el = document.querySelector(route.hash)
					if (el) {
						const headerOffset = 120
						const elementPosition = el.getBoundingClientRect().top + window.scrollY
						const offsetPosition = elementPosition - headerOffset

						window.scrollTo({
							top: offsetPosition,
							behavior: 'smooth'
						})
					}
				}, 300)
			})
		}
	}
})
	onUnmounted(() => {
		if (import.meta.client) {
			document.removeEventListener('scroll', setScroll)
		}
	})
	useSeoMeta({
		title: 'Privacy Policy | MyCoverGenius',
		description:
			'Read our privacy policy to understand how MyCovergenius collects, uses, and protects your personal information. Your privacy is important to us, and we are committed to safeguarding your data.',
		ogTitle: 'Privacy Policy | MyCoverGenius',
		ogDescription:
			'Read our privacy policy to understand how MyCovergenius collects, uses, and protects your personal information. Your privacy is important to us, and we are committed to safeguarding your data.',
		ogType: 'website',
		articleTag: ['Privacy Policy', 'MyCoverGenius'],
		ogUrl: `${import.meta.env.VITE_BUSINESS_URL}/privacy-policy`,
		twitterTitle: 'Privacy Policy | MyCoverGenius',
		twitterDescription:
			'Read our privacy policy to understand how MyCovergenius collects, uses, and protects your personal information. Your privacy is important to us, and we are committed to safeguarding your data.',
		twitterSite: '@mycovergenius',
		twitterAppUrlGoogleplay: `${import.meta.env.VITE_BUSINESS_URL}/privacy-policy`,
		twitterAppUrlIphone: `${import.meta.env.VITE_BUSINESS_URL}/privacy-policy`,
		robots: {
			all: 'true',
			follow: 'true',
			index: 'true'
		}
	})
</script>

<template>
	<div class="wrapper">
		<ui-pad :ui="{ margin: 'md:mt-10', paddingInline: 'md:px-10 px-3' }">
			<article class="md:flex items-stretch justify-between">
				<div class="h-full sticky basis-3/12 top-32 left-0 md:pr-3 lg:block hidden">
					<div
						class="border-r pt-8 border-gray-300 invisible-scrollbar max-h-screen space-y-12 overflow-y-auto lg:pb-24 flex-1 max-w-[350px]"
					>
						<client-only>
							<ul class="space-y-2 list-table">
								<li
									v-for="(item, index) in privacyContent"
									:key="item.key"
									class="border-l-[6px] border-transparent max-w-[300px] transition py-1 rounded-md pl-5 list-inside"
									:class="[item.key === inView ? 'active' : '!text-gray-700']"
								>
									<span class="font-semibold text-gray-250">{{ index + 1 }}. </span>
									<nuxt-link
										:to="{ hash: `#${item.key}` }"
										:class="[
											item.key === inView ? '!text-main' : '!text-gray-500',
											'font-semibold'
										]"
										replace
										v-html="item.name"
									/>
									<div>
										<template v-if="item.subList">
											<template
												v-for="(subItem, subIndex) in item.subList"
												:key="subIndex + index"
											>
												<div
													class="text-gray-500 text-xs inline-flex gap-1.5 pt-[15px] pl-2 font-medium"
												>
													<span> {{ index + 1 }}.{{ subIndex + 1 }} </span>
													<p>
														<nuxt-link
															:to="{ hash: `#${subItem.key}` }"
															:class="[
																subItem.key === inView ? '!text-main' : '!text-gray-700'
															]"
															replace
															v-html="subItem.name"
														/>
													</p>
												</div>
												<br />
											</template>
										</template>
									</div>
								</li>
							</ul>
						</client-only>
					</div>
				</div>

				<div class="flex-1 flex flex-col w-full mx-auto lg:max-w-[900px]">
					<div class="w-full">
						<div class="pb-5 pt-3 w-full" ref="article-wrapper">
							<div
								v-for="(item, index) in privacyContent"
								:key="item.name"
								class="pb-10 snap-start content-list"
							>
								<div>
									<h3
										class="font-[700] mb-3 sm:text-[2em] sm:leading-[38.4px] text-[1.5em] leading-[28.8px] pt-5 text-blue-950"
										:id="item.key"
									>
										{{ index + 1 }}. {{ item.name }}
									</h3>

									<div>
										<div
											class="text-slate-700 text-base sm:text-xl font-medium pb-2 sm:leading-[140%] sublist"
											v-html="item.content"
										/>
										<template v-if="item.subList">
											<div
												v-for="(subItem, subIndex) in item.subList"
												:key="subIndex + index"
												class="pl-1"
												:id="subItem.key"
											>
												<h4
													class="font-semibold mb-3 sm:text-[1.5em] text-[1em] pt-3 text-blue-950"
												>
													{{ index + 1 }}.{{ subIndex + 1 }} {{ subItem.name }}
												</h4>
												<div
													class="text-slate-700 text-lg sm:text-xl font-medium leading-[32px] sm:leading-loose pl-5 sublist"
													v-html="subItem.content"
												/>
											</div>
										</template>
									</div>
								</div>
							</div>
						</div>
					</div>
				</div>
			</article>
		</ui-pad>
	</div>
</template>

<style scoped>
	.active {
		@apply font-semibold text-base border-main;
	}
	:global(.sublist) {
		margin-left: 8px;
		padding-left: 8px;
		margin-top: 20px;
		list-style-position: outside;
	}
	:global(.sublist li) {
		list-style-type: disc;
	}
	.content-list li {
		list-style-position: outside;
	}
	:global(h3[id]),
:global(.content-list [id]) {
	scroll-margin-top: 120px;
}
</style>
